<template>
  <!-- Pins the search + switchers row (or the bulk-actions bar that replaces
       it) below the app bar while the page scrolls, so filters and sorting
       stay reachable without scrolling back to the top.
       Carries no gutter of its own: the slotted content is already a `v-col`,
       so the original spacing is preserved and the opaque background covers
       the whole column box. -->
  <v-col cols="12" class="StickyFilterBar pa-0" :style="offsetStyle">
    <!-- Owner branding on mobile / in the extension popup. It rides along with
         this bar so it stays clear of the notch and stays on screen while the
         page scrolls. See useBrandingPlacement. -->
    <BrandingHeader v-if="branding" :branding="branding" class="StickyFilterBar__branding" />
    <slot />
  </v-col>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, shallowRef } from 'vue'
import BrandingHeader from '@/components/global/BrandingHeader.vue'
import { resolveStickyTop } from '@/utils/sticky-offset'

const APP_BAR_SELECTOR = '.v-app-bar'

// The safe-area spacer App.vue renders on notched devices. It is fixed and
// paints over the status strip, so the bar has to pin below it, not at 0.
const SYSTEM_BAR_SELECTOR = '.v-system-bar'

defineProps({
  // Resolved branding to pin above the slotted row, or null. Use
  // `pinnedBranding` from @/composables/useBrandingPlacement.
  branding: {
    type: Object,
    default: null,
  },
})

// The app bar slides over ~200ms, which outlasts the scroll event that started
// it, so keep sampling for a short tail rather than reading a single frame.
const SETTLE_FRAMES = 20

// App bars are async route components, so on first paint there may be nothing
// to measure yet. Watch longer at mount to catch one that arrives late.
const MOUNT_FRAMES = 120

const offset = shallowRef(null)

let frame = null
let remainingFrames = 0

const offsetStyle = computed(() => (offset.value === null ? null : { top: `${offset.value}px` }))

const sample = () => {
  const appBar = document.querySelector(APP_BAR_SELECTOR)
  const systemBar = document.querySelector(SYSTEM_BAR_SELECTOR)
  const next = resolveStickyTop(appBar?.getBoundingClientRect(), systemBar?.getBoundingClientRect())

  // A moving app bar means the slide is still in flight -- keep the tail alive.
  if (next !== offset.value) {
    offset.value = next
    remainingFrames = SETTLE_FRAMES
  }
}

const tick = () => {
  sample()
  frame = remainingFrames-- > 0 ? requestAnimationFrame(tick) : null
}

const schedule = (frames = SETTLE_FRAMES) => {
  remainingFrames = Math.max(remainingFrames, frames)
  if (frame === null) frame = requestAnimationFrame(tick)
}

// The app bar slides via a `transform` transition that can start well after the
// scroll event that triggered it, so watch the transition itself rather than
// relying on the scroll tail alone -- otherwise the offset goes stale mid-slide.
const onAppBarTransition = (event) => {
  if (event.target?.classList?.contains('v-app-bar')) schedule()
}

// Wrapped: passing `schedule` straight to addEventListener would feed the Event
// object in as the frame budget.
const onViewportChange = () => schedule()

onMounted(() => {
  // Scheduled rather than sampled once: the app bar can mount after this bar on
  // routes that load it asynchronously.
  schedule(MOUNT_FRAMES)
  window.addEventListener('scroll', onViewportChange, { passive: true })
  window.addEventListener('resize', onViewportChange, { passive: true })
  document.addEventListener('transitionrun', onAppBarTransition, true)
  document.addEventListener('transitionend', onAppBarTransition, true)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', onViewportChange)
  window.removeEventListener('resize', onViewportChange)
  document.removeEventListener('transitionrun', onAppBarTransition, true)
  document.removeEventListener('transitionend', onAppBarTransition, true)
  if (frame !== null) cancelAnimationFrame(frame)
})
</script>

<style lang="scss" scoped>
// The desktop-only `<v-col><v-divider class="my-2" /></v-col>` that this bar's
// border replaces also carried the vertical rhythm below the bar:
// 12px col gutter + 8px divider margin + 1px rule + 8px + 12px.
// Reproduced as a margin so desktop spacing is unchanged -- margin sits outside
// the border box, so the opaque plane and the border itself do not move.
$replaced-divider-height: 41px;

.StickyFilterBar {
  position: sticky;
  // Fallback only. The offset is normally driven by the inline style above,
  // measured from the app bar itself -- `--v-layout-top` reports 0 for an app
  // bar using `scroll-behavior="hide"`, even though that mode keeps the
  // extension (the tabs row) on screen, which would park this bar underneath it.
  top: var(--v-layout-top, 0px);
  // Do NOT add `transition: top` here to chase the app bar's own slide. On a
  // sticky element the used value of `top` changes as you scroll, so the
  // transition retriggers every frame and never settles -- and transitions
  // outrank author `!important` in the cascade, so the perpetually-running
  // animation pins `top` near its starting 0px. The bar then parks at the very
  // top of the viewport, hidden behind the app bar, until you scroll back up.
  // Above page content, below the desktop app bar (2), AppBottomNav (3) and
  // StickyActionBar (3). Not `auto`: sticky paints in the positioned layer in
  // tree order, so elevated list cards would otherwise paint over the bar.
  z-index: 1;
  // Same color as the page background, theme-aware (light/dark).
  background-color: rgb(var(--v-theme-neutral0));
  // Replaces the desktop-only <v-divider color="neutral50"> that used to sit
  // directly below the bar.
  border-bottom: 1px solid rgb(var(--v-theme-neutral50));

  // Matches the breakpoint the replaced divider was gated on (`mdAndUp`). The
  // extension popup reported as desktop but is never this wide, so it keeps the
  // tighter spacing it had before without needing its own branch.
  @include md-up {
    margin-bottom: $replaced-divider-height;
  }
}

// The branding band was edge to edge when it was its own app bar. This bar sits
// inside the page container (`pa-4`) but is hoisted 12px back out by the row's
// negative gutter, so 4px of bleed restores the edge-to-edge look.
// Not `100vw`: that counts the scrollbar too and would add horizontal overflow
// on a narrow desktop window, which reports as mobile.
$container-bleed: 4px;

.StickyFilterBar__branding {
  margin-inline: -$container-bleed;
}
</style>
